import React from 'react'
import styled from 'styled-components'
import colors from '@lmc/common/constants'
import { useSelector } from 'react-redux'
import _ from 'lodash'
import { GoalStatus, User } from '@lmc/ts-common'
import GoalProgress from './GoalProgress'
import dayjs from 'dayjs'
import {
  getTagColorByStatus,
  TagUpperCase,
} from '../../../../Residents/screens/ResidentsGoalsScreen/components/GoalsList/GoalItem'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from '../../../../../locale'
import useGoalDueDate from '../hooks/useGoalDueDate'
import GoalOverdueIcon from '../icons/GoalOverdueIcon'
import GoalInTimeIcon from '../icons/GoalInTimeIcon'
import { Text } from '@lmc/ui-kit/src/general'
import { goalReportsConstants } from '../../../../../locale/goalReports'
import { GoalSummary } from '../types'

const GoalReportsRow = ({
  goal,
  onClick,
  lightVariant = false,
}: {
  goal: GoalSummary
  onClick?: (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => void
  lightVariant?: boolean
}) => {
  const carers = useSelector(
    (state: { data: { carers: User[] } }) => state.data.carers
  )

  const carer = _.find(carers, { id: goal?.reviewedBy })
  const { t } = useTranslation(NAMESPACE.GOAL_REPORTS)

  const { formattedDueDate, icon, textColor } = useGoalDueDate(
    goal.dueDate,
    goal.status
  )
  const getName = (item: any) =>
    item ? `${item.name.first} ${item.name.last}` : ''

  const reviewDate = dayjs(goal.lastReviewedDate).format('DD/MM/YY · HH:mm')
  const reviewedBy = getName(carer)
  const subtitle = `Reviewed ${reviewDate} · ${reviewedBy}`

  const statusTagColor = getTagColorByStatus(goal?.status as GoalStatus)

  return (
    <Container onClick={onClick} lightVariant={lightVariant}>
      <GoalProgress percentage={goal.progress} status={goal.status} />
      <ContentContainer>
        <GoalTitle level={1} weight="bold">
          {goal.title}
        </GoalTitle>
        <GoalSubtitle level={2} weight="medium">
          {subtitle}
        </GoalSubtitle>
      </ContentContainer>
      <StatusContainer>
        {formattedDueDate && (
          <DueDateContainer>
            <DueDate color={textColor} level={1}>
              {formattedDueDate}
            </DueDate>
            {icon === 'overdue' ? (
              <GoalOverdueIcon />
            ) : icon === 'inTime' ? (
              <GoalInTimeIcon />
            ) : null}
          </DueDateContainer>
        )}

        <TagUpperCase color={statusTagColor}>
          {t(goalReportsConstants[goal.status as GoalStatus])}
        </TagUpperCase>
      </StatusContainer>
    </Container>
  )
}

export default GoalReportsRow

const Container = styled.div<{ lightVariant: boolean }>`
  background-color: ${({ theme, lightVariant }) =>
    lightVariant ? theme.color.neutral.color11 : theme.color.neutral.color10};
  display: flex;
  border-radius: 16px;
  padding: 16px 24px;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  cursor: pointer;
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
`

const GoalTitle = styled(Text)`
  color: ${colors.black};
`

const GoalSubtitle = styled(Text)`
  color: ${(props) => props.theme.color?.neutral.color4};
`

const StatusContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`

const DueDate = styled(Text)<{ color: string }>`
  color: ${(p) => p.color};
`

const DueDateContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`
